import { createApi, fetchBaseQuery, retry } from '@reduxjs/toolkit/query/react';
import { config } from '../../../config';
import { LocalStorageKeys, getTransformedDataForPagination, objectToQueryParams } from '../../../utils';
import { TagInfo, Association } from '../../../interfaces';

interface ParentReference {
    object_id: string; // Must follow pattern: ^[a-f\d]{24}$
    object_type: string;
}

interface Link {
    object_ids: string[]; // Each must follow pattern: ^[a-f\d]{24}$
    object_type: string;
}

interface TaskBase {
    closed?: boolean; // Default: false
    deadline?: string; // ISO 8601 date-time format
    description?: string;
    hours_estimated?: number;
    name: string; // Required, max length: 500
    tags_info?: Partial<TagInfo>[];
    parent: ParentReference;
    links?: Link[];
    unlinks?: Link[];
}

export interface ITask extends TaskBase {
    hours_spent?: number; // ReadOnly
    created_time?: string; // ISO 8601 date-time format
    created_user?: string;
    created_user_id?: string;
    modified_time?: string; // ISO 8601 date-time format
    modified_user?: string;
    modified_user_id?: string;
    sequence_id?: string; // ReadOnly
    version?: number; // ReadOnly
    _id: string; // Must follow pattern: ^[a-f\d]{24}$
    associations?: Association[]; // ReadOnly
}


interface TaskGetMethodParams {
    search?: string, parent_object_type?: string, parent_object_id?: string,
    object_type?: string, object_id?: string, object_sequence_id?: string,
    tag_id?: string, tag_group_id?: string,
    page_no?: number, page_size?: number,
    sort_by?: string, sort_order?: string,
    user_id?: string
}

interface SuccessResponse {
    sequence_id?: string; // ReadOnly
    version?: number; // ReadOnly
    _id: string; // Required, Must follow pattern: ^[a-f\d]{24}$
    message: string;
    url?: string; // Optional URL for document upload/download
}

export const taskAPI = createApi({
    reducerPath: "taskAPI",
    baseQuery: retry(fetchBaseQuery({
        baseUrl: config.api_url + "task",
        prepareHeaders: (headers, { getState }) => {
            const token = localStorage.getItem(LocalStorageKeys.authToken);

            // If we have a token set in state, let's assume that we should be passing it.
            if (token) {
                headers.set('authorization', `Bearer ${token}`)
            }

            return headers
        },
    }), { maxRetries: 1 }),
    endpoints: (builder) => ({
        // Get Tasks API
        getTasks: builder.query<{ data: ITask[], totalCount: number }, TaskGetMethodParams>({
            query: (payload) => ({ url: objectToQueryParams(payload), method: "GET" }),
            transformResponse: (data: ITask[], meta: { request: Request; response?: Response }) => {
                return getTransformedDataForPagination(data, meta?.response);
            },
        }),

        // Get Task by ID API
        getTaskById: builder.query<ITask, string>({
            query: (id) => ({ url: `/${id}`, method: "GET" }),
        }),

        // Create Task API
        createTask: builder.mutation<SuccessResponse, TaskBase>({
            query: (payload) => ({ method: "POST", body: payload }),
        }),

        // Update Task API
        updateTask: builder.mutation<SuccessResponse, { id: string, payload: TaskBase, update_version?: boolean }>({
            query: (payload) => ({ url: `/${payload.id}?update_version=${payload.update_version ? true : false}`, method: "PUT", body: payload.payload }),
        }),

        // Delete Task API
        deleteTask: builder.mutation<string, string>({
            query: (id) => ({ url: `/${id}`, method: "DELETE" }),
        }),

        // Duplicate Task API
        duplicateTask: builder.mutation<SuccessResponse, string>({
            query: (id) => ({ method: "POST", url: `/${id}` }),
        }),
    })
})

// Export hooks for usage in functional components
export const {
    useGetTasksQuery, useLazyGetTasksQuery, useGetTaskByIdQuery, useLazyGetTaskByIdQuery,
    useCreateTaskMutation, useUpdateTaskMutation, useDeleteTaskMutation, useDuplicateTaskMutation
} = taskAPI